import { useDataProvider } from "@exonn/base";
import {
  type DefaultError,
  useMutation,
  type UseMutationOptions,
  useQueryClient,
} from "@tanstack/react-query";
import { useResourceQueries } from "@exonn/base";
import { useSnackbar } from "src/notifications/SnackbarProvider.ts";
import { useTranslation } from "react-i18next";
import { isUnauthorizedError } from "src/lib/axios/axios.utils.ts";
import { getResourceI18nNamespace } from "src/system/getResourceI18nNamespace.ts";
import { TranslationNamespaceEnum } from "src/lib/i18next/types.ts";

export type ValidDeleteOneData = any;

export const useDeleteOne = <
  Data extends ValidDeleteOneData = ValidDeleteOneData,
>({
  resource,
  additionalHeaders,
  ...options
}: {
  resource: string;
  additionalHeaders?: Record<string, any>;
} & UseMutationOptions<
  Data,
  DefaultError,
  {
    id: string;
    data?: Data;
    errorNotification?: {
      message: string;
      flash?: boolean;
    };
    successNotification?: {
      message: string;
      flash?: boolean;
    };
    disableErrorNotification?: boolean;
    disableSuccessNotification?: boolean;
  }
>) => {
  const dataProvider = useDataProvider();
  const queryClient = useQueryClient();
  const snackbar = useSnackbar();
  const queries = useResourceQueries();
  const { t } = useTranslation();
  return useMutation({
    mutationKey: [resource, "delete"],
    mutationFn: async (params) => {
      const { id, data } = params;
      return await dataProvider.deleteOne({
        resource: resource,
        meta: {
          additionalHeaders,
        },
        id,
        data,
      });
    },
    ...options,
    onError: (error, variables, context) => {
      const { errorNotification, disableErrorNotification } = variables;
      if (!disableErrorNotification) {
        const defaultMessage = isUnauthorizedError<{
          message: string;
        }>(error)
          ? error.response?.data.message
          : t("notifications.delete-failed", {
              ns: TranslationNamespaceEnum.Common,
            });

        const { message = defaultMessage, flash = true } =
          errorNotification || {};

        snackbar.error({
          flash,
          message,
        });
      }
      options?.onError?.(error, variables, context);
    },
    onSuccess: (data, variables, context) => {
      queryClient.invalidateQueries({
        queryKey: queries.lists({
          resource,
        }),
      });
      queryClient.removeQueries({
        queryKey: queries.detail({
          id: variables.id,
          resource,
        }),
      });
      const { successNotification, disableSuccessNotification } = variables;
      if (!disableSuccessNotification) {
        const {
          // Перевод модуля, иначе общий (common.json).
          message = t(`notifications.deleted`, {
            ns: [
              getResourceI18nNamespace(resource),
              TranslationNamespaceEnum.Common,
            ],
          }),
          flash = true,
        } = successNotification || {};
        snackbar.success({
          flash,
          message,
        });
      }

      options?.onSuccess?.(data, variables, context);
    },
  });
};
